// src/services/oneTimeCodeService.js
// Production-grade one-time code helper that talks to the GUHSO API.
// The prior development helper stored codes locally so engineers could
// verify the flow without email infrastructure. This version calls the
// live API so subscribers receive codes through the configured delivery
// channel (email, SMS, etc.).

import { OT_API_URL, makeCorsUrl } from '../api';

const REQUEST_ENDPOINT = '/auth/one-time-code/request';
const VERIFY_ENDPOINT = '/auth/one-time-code/verify';

function normaliseEmail(email) {
  return (email || '').trim().toLowerCase();
}

async function parseJson(response) {
  const text = await response.text();
  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    console.warn('oneTimeCodeService: unable to parse JSON response', error);
    return {};
  }
}

function normalisePayload(payload = {}) {
  return {
    code: payload.code || payload.previewCode || payload.preview_code || null,
    message: payload.message || payload.detail || null,
    expiresAt: payload.expiresAt || payload.expires_at || null,
    resendAfter: payload.resendAfter || payload.resend_after || null,
    channel: payload.channel || payload.delivery_channel || 'email',
    sessionToken: payload.sessionToken || payload.session_token || null,
    subscriber: payload.subscriber || null,
    token: payload.token || null,
    user: payload.user || null,
  };
}

function buildUrl(path) {
  return makeCorsUrl(OT_API_URL, path);
}

function buildDirectUrl(path) {
  return `${OT_API_URL}${path}`;
}

function buildHeaders(additionalHeaders) {
  return {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };
}

async function postWithCorsFallback(path, body, headers = {}) {
  const init = {
    method: 'POST',
    headers: buildHeaders(headers),
    body: JSON.stringify(body),
  };

  const proxiedUrl = buildUrl(path);

  try {
    return await fetch(proxiedUrl, init);
  } catch (error) {
    const directUrl = buildDirectUrl(path);
    const canFallbackToDirect = directUrl !== proxiedUrl;

    if (!canFallbackToDirect) {
      throw error;
    }

    return fetch(directUrl, init);
  }
}

export async function requestOneTimeCode(email) {
  const normalisedEmail = normaliseEmail(email);
  if (!normalisedEmail) {
    throw new Error('A valid email address is required to request a code.');
  }

  let response;
  try {
    response = await postWithCorsFallback(REQUEST_ENDPOINT, {
      email: normalisedEmail,
    });
  } catch (error) {
    throw new Error(
      'Unable to reach the sign-in service right now. Please check your connection and try again.'
    );
  }

  const payload = normalisePayload(await parseJson(response));

  if (!response.ok) {
    throw new Error(
      payload.message ||
        'We were unable to send your one-time code. Please try again in a moment.'
    );
  }

  return {
    ...payload,
    message:
      payload.message ||
      `A six-digit security code is on its way to ${normalisedEmail}. Check your inbox within the next few minutes.`,
  };
}

export async function verifyOneTimeCode(email, code, sessionToken) {
  const normalisedEmail = normaliseEmail(email);
  const normalisedCode = (code || '').trim();

  if (!normalisedEmail || !normalisedCode) {
    return {
      success: false,
      message: 'Both your email and the six-digit code are required.',
    };
  }

  const payloadBody = {
    email: normalisedEmail,
    code: normalisedCode,
  };

  if (sessionToken) {
    payloadBody.sessionToken = sessionToken;
  }

  let response;
  try {
    response = await postWithCorsFallback(VERIFY_ENDPOINT, payloadBody);
  } catch (error) {
    return {
      success: false,
      message:
        'Unable to reach the sign-in service right now. Please check your connection and try again.',
    };
  }

  const payload = normalisePayload(await parseJson(response));

  if (!response.ok) {
    return {
      success: false,
      message:
        payload.message || 'That code could not be verified. Please request a new one.',
    };
  }

  return {
    success: true,
    message: payload.message || 'Code verified successfully.',
    sessionToken: payload.sessionToken,
    subscriber: payload.subscriber,
    token: payload.token,
    user: payload.user,
  };
}
